// ── BCHNostr — Embed builder ──────────────────────────────────────
//
// A dialog a creator opens from their Creator Support goal card. It lets them
// pick a widget type + theme, see a LIVE preview (the real widget iframe), and
// copy a plain HTML snippet to paste on any website.
//
// It generates code only — it never creates a separate goal or fundraising
// record. The widget it produces reads the creator's existing live goal data.

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Code2, Monitor, Moon, Sun, ExternalLink } from 'lucide-react';
import {
  buildEmbedUrl, buildEmbedCode, buildSupportUrl, pubkeyToNpub,
  WIDGET_TYPES, WIDGET_SIZE, EMBED_ORIGIN,
  type WidgetType, type WidgetTheme,
} from '../lib/embed';
import { GoalWidget } from './GoalWidget';
import { useGoalEmbedData } from '../hooks/useGoalEmbedData';
import { useBCHPrice } from '../hooks/useBCHPrice';

interface EmbedBuilderModalProps {
  /** The creator's pubkey (hex). The widget renders that creator's goal. */
  creatorPubkey: string;
  onClose: () => void;
}

export default function EmbedBuilderModal({ creatorPubkey, onClose }: EmbedBuilderModalProps) {
  const [type, setType] = useState<WidgetType>('card');
  const [theme, setTheme] = useState<WidgetTheme>('light');
  const [copied, setCopied] = useState(false);

  const npub = useMemo(() => pubkeyToNpub(creatorPubkey), [creatorPubkey]);

  // Live preview renders the widget INLINE (same component the real embed uses)
  // rather than through a nested iframe — a nested iframe has to boot a whole
  // second copy of the app, which can hang on a spinner in preview/dev
  // environments where the SPA fallback or service worker treats nested
  // navigations differently. Rendering inline always paints immediately.
  const { price } = useBCHPrice();
  const goal = useGoalEmbedData(creatorPubkey || undefined, price);
  const donateUrl = useMemo(() => buildSupportUrl(creatorPubkey), [creatorPubkey]);

  // "Open in a new tab" still points at the standalone widget page on the
  // current origin so it works in dev/preview too.
  const previewOrigin = typeof window !== 'undefined' ? window.location.origin : EMBED_ORIGIN;
  const previewUrl = useMemo(
    () => buildEmbedUrl(npub, { type, theme }, previewOrigin),
    [npub, type, theme, previewOrigin],
  );
  const code = useMemo(
    () => buildEmbedCode(npub, { type, theme }, EMBED_ORIGIN),
    [npub, type, theme],
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => { /* clipboard blocked — the code is selectable in the textarea */ },
    );
  };

  const size = WIDGET_SIZE[type];

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 sticky top-0 bg-popover z-10">
          <h2 className="font-bold text-base flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center">
              <Code2 className="w-4 h-4 text-white" />
            </div>
            Embed your Goal
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Add a live fundraising widget to any website. It updates automatically
            as your goal raises BCH, and “Donate with BCH” opens your support page —
            payments stay direct and non-custodial.
          </p>

          {/* Widget type */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Widget</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              {WIDGET_TYPES.map(w => (
                <button
                  key={w.id}
                  onClick={() => setType(w.id)}
                  title={w.desc}
                  className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    type === w.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {WIDGET_TYPES.find(w => w.id === type)?.desc}
            </p>
          </div>

          {/* Theme */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Theme</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
              {([
                { id: 'light' as const, label: 'Light', icon: <Sun className="w-3.5 h-3.5" /> },
                { id: 'dark' as const, label: 'Dark', icon: <Moon className="w-3.5 h-3.5" /> },
              ]).map(t => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t.id)}
                  className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    theme === t.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'
                  }`}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Live preview */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 flex items-center gap-1.5">
              <Monitor className="w-3 h-3" /> Live preview
            </label>
            <div
              className={`mt-1.5 rounded-xl border border-border p-4 flex justify-center ${
                theme === 'dark' ? 'bg-neutral-900' : 'bg-neutral-100'
              }`}
            >
              <div style={{ width: '100%', maxWidth: size.width }}>
                <GoalWidget
                  type={type}
                  theme={theme}
                  loading={goal.loading}
                  found={goal.found}
                  data={goal.data}
                  raisedSats={goal.raisedSats}
                  targetSats={goal.targetSats}
                  targetUsd={goal.targetUsd}
                  pct={goal.pct}
                  backerCount={goal.backerCount}
                  price={price}
                  donateUrl={donateUrl}
                  hasCreator={!!creatorPubkey}
                />
              </div>
            </div>
          </div>

          {/* Code */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Embed code</label>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  copied ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied!' : 'Copy code'}
              </button>
            </div>
            <textarea
              readOnly
              value={code}
              onClick={e => (e.target as HTMLTextAreaElement).select()}
              spellCheck={false}
              rows={7}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-[11px] font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none leading-relaxed"
            />
            <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
              Paste this wherever you can add HTML (a website, blog post, or
              “custom HTML” block). It’s responsive and works on mobile.
            </p>
          </div>

          {/* Open preview in a new tab */}
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs text-primary hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open widget in a new tab
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}
